import { Controller } from "@hotwired/stimulus";
import { useStore } from "stimulus-store";
import {
    getAreasForSelect,
    getBuildingsForSelect,
    getRegionsForSelect,
    getRoomsForSelect
} from "shared/locales/locales";
import { LOCALE_TYPE_AREA, LOCALE_TYPE_BUILDING, LOCALE_TYPE_REGION } from "constants/facilities_constants";
import { facilitySelectionStore } from "../../stores/facility_selection_store";
import _ from "lodash";
import {
    addItemToTable,
    getCurrentlyDisplayedFacilityType,
    removeItemFromTable,
    updateItemInTable
} from "../../stores/helpers/facility_helpers";
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications";
import { areaStore } from "../../stores/locales/area_store";

// Connects to table--facilities-table
export default class extends Controller {
    static stores = [
        areaStore,
        facilitySelectionStore
    ]
    static targets = ["table"];
    static values = {
        id: String,
        localeType: String,

        // Set by the Facilities page (settings/facilities/index.html.erb) to a short-lived, signed
        // organization-scope token. When present, it is forwarded as the X-Project-Scope-Token
        // header on every locale-list request so the tree/table shows ALL locales in the
        // organization, not just the caller's active-project locales. Left unset elsewhere,
        // the requests stay active-project scoped exactly as before.
        projectScopeToken: String
    }

    initialize() {
        useStore(this);
    }

    /**
     * Called when the table target is connected.
     * Sets up argus--table columns and loads initial data.
     */
    tableTargetConnected(element) {
        if (!this.hasTableTarget) return;

        // Defer so argus--table controller connects first
        Promise.resolve().then(() => {
            const argusTable = this._getArgusTable();
            if (!argusTable) {
                console.warn('table--facilities-table: argus--table controller not found');
                return;
            }

            // Set up columns programmatically
            argusTable._columns = [
                { field: 'name', title: 'Name', sortable: true, visible: true, class: 'locale-name' },
                { field: 'actions', title: 'Actions', sortable: false, visible: true, formatter: 'actionsColumnFormatter', width: '1%' },
                { field: 'id', title: 'ID', sortable: false, visible: false },
                { field: 'localeType', title: 'Facility Type', sortable: false, visible: false }
            ];

            // Rebuild column toggler
            const ColumnTogglerClass = argusTable.columnToggler.constructor;
            argusTable.columnToggler = new ColumnTogglerClass(
                argusTable._columns, () => argusTable._render()
            );

            // Set row attributes so locale data is accessible from the DOM
            argusTable._rowAttributesFn = (row) => {
                return {
                    'data-locale-id': row.id || '',
                    'data-locale-type': row.localeType || '',
                    'data-locale-name': row.name || ''
                }
            };

            // Set up custom buttons (Back + Add)
            argusTable.buttonsValue = [
                {
                    html: '<button type="button" class="btn btn-secondary"><i class="bi bi-arrow-90deg-left"></i> Back</button>',
                    event: 'facilitiesTableBackButtonClick'
                },
                {
                    html: '<button type="button" class="btn btn-secondary"><i class="bi bi-plus-lg"></i> Add new facility</button>',
                    event: 'facilitiesTableAddButtonClick'
                }
            ];

            // Initial render then load data
            argusTable._render();
            this.loadFacilities();

            // Double-click on a row to drill into the next level
            this.tableTarget.addEventListener('dblclick', (e) => {
                const tr = e.target.closest('tbody tr')
                if (!tr) return
                // Reuse the existing explode logic — it reads data attributes from the row
                this.explodeLocaleClicked(e)
            })
        });
    }

    /**
     * Get the argus--table controller instance
     */
    _getArgusTable() {
        if (!this.hasTableTarget) return null;
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        );
    }

    /**
     * Fetch facilities based on current selection and load into argus--table
     */
    loadFacilities() {
        const argusTable = this._getArgusTable();
        if (!argusTable) return;

        const region = this.facilitySelectionStoreValue.region.id;
        const building = this.facilitySelectionStoreValue.building.id;
        const area = this.facilitySelectionStoreValue.area.id;

        // Signal loading start; the shared argus--table overlay handles the spinner.
        argusTable.showLoading();

        if (_.isEmpty(region)) {
            getRegionsForSelect(this.projectScopeTokenValue).then((results) => {
                const formatted = this._formatData(results);
                argusTable.loadData(formatted);
                this._updateColumnsForLocaleType('region');
            }).catch((error) => {
                this._handleLoadFacilitiesError(error);
            }).finally(() => {
                argusTable.hideLoading();
            });
        } else if (!_.isEmpty(region) && _.isEmpty(building)) {
            getBuildingsForSelect(region, this.projectScopeTokenValue).then((results) => {
                const formatted = this._formatData(results);
                argusTable.loadData(formatted);
                this._updateColumnsForLocaleType('building');
            }).catch((error) => {
                this._handleLoadFacilitiesError(error);
            }).finally(() => {
                argusTable.hideLoading();
            });
        } else if (!_.isEmpty(region) && !_.isEmpty(building) && _.isEmpty(area)) {
            getAreasForSelect(region, building, this.projectScopeTokenValue).then((results) => {
                const formatted = this._formatData(results);
                argusTable.loadData(formatted);
                this._updateColumnsForLocaleType('area');

                // Store area attributes
                const updatedAreas = this.areaStoreValue;
                results.forEach((areaItem) => {
                    updatedAreas[areaItem.id] ||= areaItem;
                });
                this.setAreaStoreValue(updatedAreas);
            }).catch((error) => {
                this._handleLoadFacilitiesError(error);
            }).finally(() => {
                argusTable.hideLoading();
            });
        } else if (!_.isEmpty(region) && !_.isEmpty(building) && !_.isEmpty(area)) {
            getRoomsForSelect(region, building, area, this.projectScopeTokenValue).then((results) => {
                const formatted = this._formatData(results);
                argusTable.loadData(formatted);
                this._updateColumnsForLocaleType('room');
            }).catch((error) => {
                this._handleLoadFacilitiesError(error);
            }).finally(() => {
                argusTable.hideLoading();
            });
        } else {
            // No branch matched; nothing to fetch, so clear the loading signal.
            argusTable.hideLoading();
        }
    }

    /**
     * Surface a load failure to the user. The overlay itself is always cleared
     * by the `.finally()` on the fetch chain regardless of success or failure.
     */
    _handleLoadFacilitiesError(error) {
        const selectedLocaleTypeName = getCurrentlyDisplayedFacilityType(this.facilitySelectionStoreValue);
        console.error(`Error loading ${selectedLocaleTypeName} facilities:`, error);
        sendFailureNotification(`Failed to load ${selectedLocaleTypeName} facilities.`);
    }

    /**
     * Update table columns based on locale type
     * Only areas display the floor column
     */
    _updateColumnsForLocaleType(localeType) {
        const argusTable = this._getArgusTable();
        if (!argusTable) return;

        const baseColumns = [
            { field: 'name', title: 'Name', sortable: true, visible: true, class: 'locale-name' },
            { field: 'actions', title: 'Actions', sortable: false, visible: true, formatter: 'actionsColumnFormatter', width: '1%' },
            { field: 'id', title: 'ID', sortable: false, visible: false },
            { field: 'localeType', title: 'Facility Type', sortable: false, visible: false }
        ];

        // Insert floor column only for areas
        if (localeType === 'area') {
            baseColumns.splice(1, 0,
                { field: 'floor', title: 'Floor', sortable: true, visible: true, class: 'locale-floor' }
            );
        }

        argusTable._columns = baseColumns;
        const ColumnTogglerClass = argusTable.columnToggler.constructor;
        argusTable.columnToggler = new ColumnTogglerClass(argusTable._columns, () => argusTable._render());
        argusTable._render();
    }

    /**
     * Format API response data for the table, sorted alphabetically by name
     */
    _formatData(results) {
        return results
            .sort((a, b) => (a.text || '').localeCompare(b.text || ''))
            .map(locale => {
                const data = {
                    id: locale.id,
                    name: locale.text,
                    localeType: locale.locale,
                };
                // Only include floor for areas
                if (locale.locale === 'area') {
                    data.floor = locale.floor || '-';
                }
                return data;
            });
    }

    explodeLocaleClicked(event) {
        event.preventDefault();

        const tableRow = event.target.closest('tr');
        const localeId = tableRow.dataset.localeId;
        const localeType = tableRow.dataset.localeType;
        const localeName = tableRow.dataset.localeName;

        if (localeType === LOCALE_TYPE_REGION) {
            this.setFacilitySelectionStoreValue({
                region: { id: localeId, name: localeName },
                building: { id: '', name: '' },
                area: { id: '', name: '' },
                room: { id: '', name: '' }
            });
        } else if (localeType === LOCALE_TYPE_BUILDING) {
            this.setFacilitySelectionStoreValue({
                region: { id: this.facilitySelectionStoreValue.region.id, name: this.facilitySelectionStoreValue.region.name },
                building: { id: localeId, name: localeName },
                area: { id: '', name: '' },
                room: { id: '', name: '' }
            });
        } else if (localeType === LOCALE_TYPE_AREA) {
            this.setFacilitySelectionStoreValue({
                region: { id: this.facilitySelectionStoreValue.region.id, name: this.facilitySelectionStoreValue.region.name },
                building: { id: this.facilitySelectionStoreValue.building.id, name: this.facilitySelectionStoreValue.building.name },
                area: { id: localeId, name: localeName },
                room: { id: '', name: '' }
            });
        }

        // Refresh the table
        this.loadFacilities();
    }

    handleBackButtonClick(event) {
        const selectedFacilities = this.facilitySelectionStoreValue;
        const region = selectedFacilities.region.id;
        const building = selectedFacilities.building.id;
        const area = selectedFacilities.area.id;
        const regionName = selectedFacilities.region.name;
        const buildingName = selectedFacilities.building.name;

        if (_.isEmpty(region)) {
            return;
        }

        if (!_.isEmpty(region) && _.isEmpty(building)) {
            this.setFacilitySelectionStoreValue({
                region: { id: '', name: '' },
                building: { id: '', name: '' },
                area: { id: '', name: '' },
                room: { id: '', name: '' }
            });
        } else if (!_.isEmpty(region) && !_.isEmpty(building) && _.isEmpty(area)) {
            this.setFacilitySelectionStoreValue({
                region: { id: region, name: regionName },
                building: { id: '', name: '' },
                area: { id: '', name: '' },
                room: { id: '', name: '' }
            });
        } else if (!_.isEmpty(region) && !_.isEmpty(building) && !_.isEmpty(area)) {
            this.setFacilitySelectionStoreValue({
                region: { id: region, name: regionName },
                building: { id: building, name: buildingName },
                area: { id: '', name: '' },
                room: { id: '', name: '' }
            });
        }

        // Refresh the table
        this.loadFacilities();
    }

    deleteLocale(event) {
        event.preventDefault();

        const tableRow = event.target.closest('tr');
        const localeId = tableRow.dataset.localeId;
        const localeType = tableRow.dataset.localeType;
        const localeName = tableRow.dataset.localeName;

        const confirmationMessage = `Are you sure you want to delete the ${localeName} ${localeType} and its devices?`;

        if (!confirm(confirmationMessage)) {
            return;
        }

        const url = `/api/locales/${_.toLower(localeType)}s/delete`;
        const formData = new FormData();
        formData.append('id', localeId);

        $.ajax({
            url: url,
            method: 'DELETE',
            dataType: 'json',
            format: 'json',
            data: formData,
            processData: false,
            contentType: false,
        }).promise().then((result) => {
            const {id, localeType, name} = result;
            removeItemFromTable(id);
            sendSuccessNotification(`Successfully deleted ${localeType} ${name}`);
        }).catch((error) => {
            const selectedLocaleTypeName = getCurrentlyDisplayedFacilityType(this.facilitySelectionStoreValue);
            sendFailureNotification(`Failed to delete a ${selectedLocaleTypeName}.\n${error.responseJSON.message}`);
        });
    }

    updateLocaleButtonClicked(event) {
        event.preventDefault();

        const tableRow = event.target.closest('tr');
        const localeId = tableRow.dataset.localeId;
        const localeType = tableRow.dataset.localeType;
        const localeName = tableRow.dataset.localeName;

        window.dispatchEvent(new CustomEvent('updateLocaleClicked', {
            detail: { localeType: localeType, id: localeId, name: localeName },
            bubbles: true
        }));
    }

    addLocaleToTable(event) {
        const localeType = event.detail.localeType;
        const id = event.detail.id;
        const name = event.detail.name;
        addItemToTable(localeType, id, name);
    }

    removeLocaleFromTable(event) {
        const localeType = event.detail.localeType;
        const id = event.detail.id;
        removeItemFromTable(localeType, id);
    }

    updateLocaleInTable(event) {
        const localeType = event.detail.localeType;
        const id = event.detail.id;
        const name = event.detail.name;
        updateItemInTable(localeType, id, name);
    }
}
